import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

const isActive = (pathname, target) =>
    pathname === target || pathname.startsWith(`${target}/`);

function GeneralSidebar(props) {
    const location = useLocation();
    const [menuList, setMenuList] = useState([]);
    const [menuIDList, setMenuIDList] = useState([]);
    const [expandedMenus, setExpandedMenus] = useState(new Set());
    const [headerHeight, setHeaderHeight] = useState(140);

    // Measure header height dynamically
    useEffect(() => {
        const updateHeaderHeight = () => {
            const header = document.getElementById('kt_header');
            if (header) {
                setHeaderHeight(header.offsetHeight);
            }
        };

        // Initial measurement
        updateHeaderHeight();

        // Update on resize
        window.addEventListener('resize', updateHeaderHeight);

        // Use ResizeObserver for more accurate tracking
        const header = document.getElementById('kt_header');
        if (header) {
            const resizeObserver = new ResizeObserver(() => {
                updateHeaderHeight();
            });
            resizeObserver.observe(header);

            return () => {
                window.removeEventListener('resize', updateHeaderHeight);
                resizeObserver.disconnect();
            };
        }

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, []);

    useEffect(() => {
        if (props.permissionData.length > 0) {
            // Get unique menu names (main menu items)
            const menu_key = "menu_name";
            const menu_list = [
                ...new Map(props.permissionData.map(menu =>
                    [menu[menu_key], menu])).values()
            ];
            setMenuList(menu_list);

            // Get unique menu IDs
            const menu_id_list = [
                ...new Set(props.permissionData.map((item) => item.menu_id)),
            ];
            setMenuIDList(menu_id_list);

            // Expand menus that contain the active route
            const activeMenus = new Set();
            menu_list.forEach(menu => {
                const sub_menu_list = props.permissionData.filter(p => p.menu_id === menu.menu_id);
                const hasActiveItem = sub_menu_list.some(sub =>
                    isActive(location.pathname, sub.sub_menu_link)
                );
                if (hasActiveItem) {
                    activeMenus.add(menu.menu_id);
                }
            });
            setExpandedMenus(activeMenus);
        }
    }, [props.permissionData, location.pathname]);

    const toggleMenu = (menuId) => {
        setExpandedMenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(menuId)) {
                newSet.delete(menuId);
            } else {
                newSet.add(menuId);
            }
            return newSet;
        });
    };

    return (
        <aside
            className="hidden lg:flex fixed left-0 w-72 flex-shrink-0 flex-col border-r border-slate-800 dark:border-slate-700 bg-slate-900 dark:bg-slate-800 text-slate-100 transition-colors duration-200 z-40"
            style={{
                top: `${headerHeight}px`,
                height: `calc(100vh - ${headerHeight}px)`
            }}
            aria-label="Primary navigation"
        >
            <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-800 dark:border-slate-700">
                <div className="h-10 w-10 rounded-full bg-indigo-600 flex items-center justify-center text-sm font-bold">
                    HR
                </div>
                <div>
                    <p className="text-sm uppercase tracking-wide text-slate-400 dark:text-slate-500">Payroll Suite</p>
                    <p className="text-lg font-semibold text-white">Control Center</p>
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                {menuList.length > 0 ? (
                    menuList.map((menu) => {
                        let sub_menu_list = props.permissionData.filter(p => p.menu_id === menu.menu_id);

                        // Manually inject 'Salary Structure View' into Employee Management
                        if (menu.menu_name === 'Employee Management') {
                            if (!sub_menu_list.find(s => s.sub_menu_link === '/employee-management/salary-structure-view')) {
                                // Create a mutable copy if needed, but array push helps here if we treat it as mutable
                                // To be safe and react-clean:
                                sub_menu_list = [...sub_menu_list, {
                                    menu_id: menu.menu_id,
                                    sub_menu_name: 'Salary Structure View',
                                    sub_menu_link: '/employee-management/salary-structure-view'
                                }];
                            }
                            if (!sub_menu_list.find(s => s.sub_menu_link === '/employee-management/step-upgrade')) {
                                sub_menu_list = [...sub_menu_list, {
                                    menu_id: menu.menu_id,
                                    sub_menu_name: 'Employee Step Upgrade',
                                    sub_menu_link: '/employee-management/step-upgrade'
                                }];
                            }
                        }

                        // if (menu.menu_name === 'Payroll Management' || menu.menu_name === 'Human Resources' || menu.menu_name === 'Payroll') {
                        //     if (!sub_menu_list.find(s => s.sub_menu_link === '/human-resources/duplicate-allowance-and-deductions')) {
                        //         sub_menu_list = [...sub_menu_list, {
                        //             menu_id: menu.menu_id,
                        //             sub_menu_name: 'Sanitize Allowance & Deduction Entries',
                        //             sub_menu_link: '/human-resources/duplicate-allowance-and-deductions'
                        //         }];
                        //     }
                        //     if (!sub_menu_list.find(s => s.sub_menu_link === '/human-resources/earnings-and-deductions-management')) {
                        //         sub_menu_list = [...sub_menu_list, {
                        //             menu_id: menu.menu_id,
                        //             sub_menu_name: 'Earnings & Deductions Management',
                        //             sub_menu_link: '/human-resources/earnings-and-deductions-management'
                        //         }];
                        //     }
                        // }



                        const isExpanded = expandedMenus.has(menu.menu_id);
                        const hasActiveItem = sub_menu_list.some(sub =>
                            isActive(location.pathname, sub.sub_menu_link)
                        );

                        return (
                            <div key={menu.menu_id} className="space-y-1">
                                {/* Menu Header - Clickable to toggle */}
                                <button
                                    onClick={() => toggleMenu(menu.menu_id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleMenu(menu.menu_id);
                                        }
                                    }}
                                    className={[
                                        "w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900",
                                        hasActiveItem
                                            ? "bg-slate-800 dark:bg-slate-700 text-white shadow-sm"
                                            : "text-slate-300 dark:text-slate-400 hover:bg-slate-800 dark:hover:bg-slate-700 hover:text-white hover:shadow-sm",
                                    ].join(" ")}
                                    aria-expanded={isExpanded}
                                    aria-controls={`submenu-${menu.menu_id}`}
                                    aria-label={`Toggle ${menu.menu_name} menu`}
                                >
                                    <span className="uppercase tracking-wide">{menu.menu_name}</span>
                                    {isExpanded ? (
                                        <ChevronDownIcon className="h-4 w-4 transition-transform duration-200" aria-hidden="true" />
                                    ) : (
                                        <ChevronRightIcon className="h-4 w-4 transition-transform duration-200" aria-hidden="true" />
                                    )}
                                </button>

                                {/* Submenu Items - Collapsible */}
                                {isExpanded && (
                                    <ul
                                        id={`submenu-${menu.menu_id}`}
                                        className="ml-2 space-y-1 border-l-2 border-slate-700 dark:border-slate-600 pl-2"
                                        role="menu"
                                        aria-label={`${menu.menu_name} submenu`}
                                    >
                                        {sub_menu_list.length > 0 &&
                                            sub_menu_list.map((sub, index) => {
                                                const active = isActive(location.pathname, sub.sub_menu_link);
                                                return (
                                                    <li key={`${sub.menu_id}-${index}`} role="none">
                                                        <Link
                                                            to={sub.sub_menu_link}
                                                            className={[
                                                                "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900",
                                                                active
                                                                    ? "bg-indigo-600 text-white shadow-sm dark:bg-indigo-500"
                                                                    : "text-slate-200 dark:text-slate-300 hover:bg-slate-800 dark:hover:bg-slate-700 hover:text-white hover:shadow-sm",
                                                            ].join(" ")}
                                                            aria-current={active ? "page" : undefined}
                                                            role="menuitem"
                                                        >
                                                            <span className="ml-1">{sub.sub_menu_name}</span>
                                                        </Link>
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                )}
                            </div>
                        );
                    })
                ) : (
                    // Fallback to default navigation if no permission data
                    <div className="space-y-2">
                        <div className="px-3 py-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                HR & Payroll
                            </p>
                        </div>
                        <ul className="space-y-1">
                            <li>
                                <Link
                                    to="/"
                                    className={[
                                        "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                                        isActive(location.pathname, "/")
                                            ? "bg-indigo-600 text-white shadow-sm dark:bg-indigo-500"
                                            : "text-slate-200 dark:text-slate-300 hover:bg-slate-800 dark:hover:bg-slate-700 hover:text-white",
                                    ].join(" ")}
                                >
                                    <span>Dashboard</span>
                                </Link>
                            </li>
                        </ul>
                    </div>
                )}
            </nav>

            <div className="px-6 py-4 border-t border-slate-800 text-sm text-slate-400">
                <p className="font-semibold text-white">Need help?</p>
                <p className="text-slate-400">Contact support or visit the help center.</p>
            </div>
        </aside>
    );
}

const mapStateToProps = (state) => {
    return {
        permissionData: state.PermissionDetails,
    };
};

export default connect(mapStateToProps, null)(GeneralSidebar);
